import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { db } from "../utils/firebase";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";

const MAX = 4;
const LIMIT = 500;

const defaultTemplates = [
  "👋 Hi {name}, hope you're doing great! 🎉 We’ve got exciting *new offers* for you!",
  "🙏 Hi {name}, thank you for choosing us! Could you share feedback here: [link]",
  "🍲 Hi {name}, we’ve added a delicious new dish! Visit today 🎉",
  "🌟 Hi {name}, thanks for being a loyal customer! Enjoy a special discount 🎁"
];

export default function ZaffloEnterpriseTemplates() {
  const location = useLocation();

  /* ✅ GET EMAIL (priority order) */
  const emailFromStorage = localStorage.getItem("user");
  const emailFromState = location?.state?.user;
  const email = emailFromStorage || emailFromState || null;

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  /* guard ref */
  const ref = email ? doc(db, "whatsappTemplates", email) : null;

  /* LOAD */
  useEffect(() => {
    if (!email) {
      console.log("No email found");
      setLoading(false);
      return;
    }

    const load = async () => {
      try {
        const snap = await getDoc(ref);
        if (snap.exists()) {
          setTemplates(snap.data().templates?.slice(0, MAX) || defaultTemplates);
        } else {
          setTemplates(defaultTemplates);
        }
      } catch (e) { console.error(e) }
      setLoading(false);
    };

    load();
  }, [email]);

  /* UPDATE */
  const update = (i, val) => {
    if (val.length > LIMIT) return;
    const arr = [...templates];
    arr[i] = val;
    setTemplates(arr);
  };

  /* ADD */
  const add = () => {
    if (templates.length >= MAX) return;
    setTemplates([...templates, ""]);
  };

  /* REMOVE */
  const remove = i => {
    setTemplates(templates.filter((_, x) => x !== i));
  };

  /* INSERT NAME */
  const insertName = i => {
    update(i, templates[i] + " {name}");
  };

  /* SAVE */
  const save = async () => {
    if (!ref) return alert("User email missing");
    setSaving(true);

    try {
      await setDoc(ref, {
        templates: templates.filter(t => t.trim()),
        updatedAt: serverTimestamp()
      });
      alert("Templates saved successfully!");
    } catch (e) {
      console.error(e);
      alert("Save failed");
    }
    setSaving(false);
  };

  /* PREVIEW */
  const preview = text => {
    return text
      ?.replace("{name}", "Rahul")
      ?.replace(/\*([^*]+)\*/g, "$1");
  };

  /* UI: Loading State */
  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="flex flex-col items-center gap-4 animate-pulse">
        <div className="h-12 w-12 bg-emerald-500 rounded-full"></div>
        <p className="text-gray-500 font-medium">Loading workspace...</p>
      </div>
    </div>
  );

  /* UI: Error State */
  if (!email) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
      <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-200 text-center max-w-md w-full">
        <div className="h-16 w-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h2 className="text-xl font-bold text-gray-800 mb-2">Authentication Error</h2>
        <p className="text-gray-500">User not found. Please log in again to manage your templates.</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      
      {/* 🚀 Sticky Header for better UX */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-6xl mx-auto px-6 py-4 flex flex-col sm:flex-row justify-between items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
              WhatsApp Templates
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Manage and customize up to {MAX} pre-written messages
            </p>
          </div>
          
          <button
            onClick={save}
            disabled={saving}
            className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-semibold shadow-sm transition-all duration-200 ${
              saving 
                ? "bg-gray-100 text-gray-400 cursor-not-allowed" 
                : "bg-emerald-600 hover:bg-emerald-700 text-white shadow-emerald-200 hover:shadow-md hover:-translate-y-0.5"
            }`}
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
            </svg>
            {saving ? "Saving Changes..." : "Save Templates"}
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 mt-8">
        
        {/* 🚀 Grid Layout */}
        <div className="grid lg:grid-cols-2 gap-6">
          {templates.map((t, i) => (
            <div key={i} className="bg-white rounded-2xl shadow-sm border border-gray-200 flex flex-col overflow-hidden transition-shadow hover:shadow-md">
              
              {/* Card Header */}
              <div className="px-5 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold text-sm">
                    {i + 1}
                  </div>
                  <h3 className="font-semibold text-gray-700">Message Slot</h3>
                </div>
                
                <button 
                  onClick={() => remove(i)} 
                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors group"
                  title="Delete Template"
                >
                  <svg className="w-5 h-5 group-hover:scale-110 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>

              {/* Editor Section */}
              <div className="p-5 flex flex-col gap-4">
                <div className="relative">
                  <textarea
                    value={t}
                    onChange={e => update(i, e.target.value)}
                    className="w-full h-32 bg-gray-50 border border-gray-200 rounded-xl p-4 text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none transition-all"
                    placeholder="Type your WhatsApp message here..."
                  />
                  
                  {/* Character Counter inside Textarea */}
                  <div className={`absolute bottom-3 right-3 text-xs font-semibold px-2 py-1 rounded-md ${
                    t?.length > 450 ? "bg-red-100 text-red-600" : "bg-gray-200 text-gray-600"
                  }`}>
                    {t?.length || 0} / {LIMIT}
                  </div>
                </div>

                {/* Editor Actions */}
                <div className="flex justify-start">
                  <button
                    onClick={() => insertName(i)}
                    className="flex items-center gap-2 text-sm font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 px-4 py-2 rounded-lg transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    Insert Name Tag
                  </button>
                </div>
              </div>

              {/* 🚀 Authentic WhatsApp Preview */}
              <div className="bg-[#EFEAE2] p-5 border-t border-gray-200 flex-grow flex flex-col justify-end min-h-[140px]">
                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-3 text-center opacity-70">
                  Customer View Preview
                </p>
                <div className="flex justify-end">
                  <div className="relative bg-[#D9FDD3] px-3 py-2 rounded-lg rounded-tr-none shadow-sm max-w-[85%]">
                    <p className="text-[14px] text-gray-800 whitespace-pre-wrap leading-relaxed">
                      {preview(t) || "Your preview will appear here..."}
                    </p>
                    <div className="text-[10px] text-gray-500 text-right mt-1 font-medium flex justify-end items-center gap-1">
                      12:00 PM
                      <svg className="w-3 h-3 text-blue-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                         <path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round"/>
                         <path d="M5 17l5-5" strokeLinecap="round" strokeLinejoin="round" opacity="0.5"/>
                      </svg>
                    </div>
                    {/* WhatsApp Tail */}
                    <div className="absolute top-0 right-[-8px] w-0 h-0 border-t-[8px] border-t-[#D9FDD3] border-r-[8px] border-r-transparent"></div>
                  </div>
                </div>
              </div>

            </div>
          ))}

          {/* 🚀 Add Template "Ghost" Card */}
          {templates.length < MAX && (
            <button
              onClick={add}
              className="min-h-[350px] border-2 border-dashed border-gray-300 rounded-2xl flex flex-col items-center justify-center text-gray-400 hover:text-emerald-600 hover:border-emerald-400 hover:bg-emerald-50/30 transition-all duration-300 group"
            >
              <div className="h-16 w-16 rounded-full bg-gray-100 group-hover:bg-emerald-100 flex items-center justify-center mb-4 transition-colors">
                <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </div>
              <p className="font-semibold text-lg">Create New Template</p>
              <p className="text-sm mt-1">({templates.length} of {MAX} used)</p>
            </button>
          )}

        </div>
      </div>
    </div>
  );
}